import styled from "@emotion/styled"
import { useRef, useLayoutEffect } from "react"
import { useConsole } from "contexts/Console"
import { motion, useMotionValue, useMotionValueEvent, useTransform, useMotionTemplate } from "framer-motion"
import getMediaQuery from "css/breakpoints"
import { useMoonCalendar, observeResize, rafDelay } from "./context"
import { Icon } from "components/icon/Icon"
import { useLocale } from "contexts/Locale"
import * as button from "css/buttons"
import { ButtonStyled } from "components/button/Button"
import MoonphaseInstructions from "./MoonphaseInstructions"
import { useEnv } from "contexts/Env"
import imageLoader from "components/media/imageLoader"

const Main = styled(motion.div)`
  @property --mc-nav-offset {
    syntax: "<length>";
    inherits: true;
    initial-value: 0px;
  }

  @property --mc-nav-alpha {
    syntax: "<number>";
    inherits: true;
    initial-value: 0;
  }
  @property --mc-nav-wrap-alpha {
    syntax: "<number>";
    inherits: true;
    initial-value: 0;
  }
  @property --mc-nav-wrap-y {
    syntax: "<percentage>";
    inherits: true;
    initial-value: 100%;
  }

  position: sticky;
  bottom: 0;
  width: 100%;
  overflow: hidden;
  margin-top: var(--mc-nav-offset);
  opacity: var(--mc-nav-alpha);
  transition: opacity 1s cubic-bezier(1, 1, 1, 1) 1.4s;

  html.prefers-reduced-motion & {
    transition-duration: 0.1s;
    transition-delay: 0s;
  }

  display: grid;
  justify-items: center;
  align-items: end;

  &.mc-nav-loaded {
    --mc-nav-alpha: 1;
  }

  pointer-events: none;
  & button {
    pointer-events: auto;
    transform: translateZ(0);
  }
`

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 160rem;
  padding-inline: var(--outer-margin);
  padding-block: 0 1.75rem;
  box-sizing: border-box;
  grid-column: 1;
  grid-row: 1;
  display: grid;
  justify-items: center;
  align-items: end;
  grid-template-columns: 1fr;
  opacity: var(--mc-nav-wrap-alpha);
  transform: translateY(var(--mc-nav-wrap-y));
  transition: opacity 1s cubic-bezier(1, 1, 1, 1) 2.4s, transform 1s cubic-bezier(0.22, 1, 0.36, 1) 2.4s;

  html.prefers-reduced-motion & {
    transition-duration: 0s, 0s;
    transition-delay: 0s, 0s;
  }

  ${getMediaQuery("l")} {
    padding-inline: 0;
    grid-template-columns: 1fr max-content 1fr;
    gap: 1.75rem;
  }

  .mc-nav-loaded & {
    --mc-nav-wrap-alpha: 1;
    --mc-nav-wrap-y: 0%;
  }
`

const Nav = styled.div`
  grid-column: 1;
  grid-row: 2;
  display: grid;
  gap: 1rem;
  grid-template-rows: max-content;
  padding-block: 1.75rem 0;
  pointer-events: auto;

  ${getMediaQuery("m")} {
    gap: 1.75rem;
  }

  ${getMediaQuery("l")} {
    grid-column: 2;
    grid-row: 1;
    padding-block: 0;
  }
`

const Guide = styled.div`
  position: relative;
  grid-column: 1;
  box-sizing: border-box;
  width: 100%;
  max-width: 32rem;

  ${getMediaQuery("l")} {
    grid-column: 3;
    justify-self: end;
//    padding-block: 0 1.75rem;
    padding-inline: 0 1.75rem;
    max-width: 400px;
  }

  > div {
    margin-block-start: 0px;
    padding-inline: 0px;
    h2 {
      font-size: 14px;
      line-height: 20px;
    }
    ul {
      font-size: 12px;
      line-height: 18px;
    }
  }
`

const NavButton = styled(ButtonStyled)`
  ${button.buttonFilled}
  position: relative;
  grid-row: 1;
  pointer-events: auto;
  span.label {
    display: none;
    clip: unset;
    width: unset;
    height: unset;
    position: relative;
    clip-path: unset;
  }
  span.moon {
    display: block;
    clip: unset;
    width: 18px;
    height: 18px;
    position: relative;
    clip-path: unset;
    display: block;
    overflow: visible;

    &::after {
      content: "";
      width: 28px;
      height: 28px;
      background-image: var(--moon-background);
      background-repeat: no-repeat;
      background-size: cover;
      display: block;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%,-50%);
    }
  }

  &:disabled {
    pointer-events: none !important;
    cursor: default !important;
  }
  @media (hover: hover) {
    :hover:disabled {
    pointer-events: none !important;
    cursor: default !important;
  }}

  &:first-of-type {
    padding-inline: 1.25rem 1rem;
  }
  &:nth-of-type(2) {
    padding-inline: 1.5rem;
  }
  &:last-of-type {
    padding-inline: 1rem 1.25rem;
  }

  ${getMediaQuery("xs")} {
    &:first-of-type {
      padding-inline: 1.5rem 1.25rem;
    }
    &:nth-of-type(2) {
      padding-inline: 1.75rem;
    }
    &:last-of-type {
      padding-inline: 1.25rem 1.5rem;
    }
  }

  ${getMediaQuery("s")} {
    &:first-of-type,  
    &:nth-of-type(2),
    &:last-of-type {
      padding-inline: var(--padding);
    }
  }
  
  ${getMediaQuery("xl")} {
    span.label {
      display: block;
    }
    span.moon {
      display: none;
    }
  }

  &:dir(rtl) svg {
    transform: scale(-1);
  }
`

const Overlay = styled.div`
  position: absolute;
  grid-row: 1/-1;
  grid-column: 1/-1;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;

  & div {
    position: absolute;
    width: 100%;
    height: 120%;
    overflow: hidden;
    background: linear-gradient(0deg, rgba(1, 14, 57, 1) 0%, rgba(1, 14, 57, 0) 100%);
    mix-blend-mode: multiply;

    @supports (background: -webkit-named-image(i)) {
      will-change: filter;
    }
  }
`

function MoonCalendarButtonImage() {
  const env = useEnv()

  const image = {
    public_id: "rolexcom/049e3b0b345c/new-watches/padellone/moon-calendar/icon-full-moon",
    width: 169,
    height: 169,
  }

  const getUrl = imageLoader({ src: image }, env)

  return (
    <span
      className="moon"
      style={{
        "--moon-background": `url(${getUrl({ width: 169 })})`,
      }}
    />
  )
}

export default function MoonCalendarNav(props) {
  const console = useConsole()
  const locale = useLocale().current.codes.ws

  const { ctx, labels } = props

  const { loaded, playing, ended, moon_time, lastFM, now, nextFM, moving } = useMoonCalendar(ctx)

  const rfmain = useRef(null)
  const prevM = useRef(null)
  const nowM = useRef(null)
  const nextM = useRef(null)

  const h = useMotionValue(0)
  const offset = useMotionTemplate`-${h}px`

  function onResize({ isize, bsize, target }) {
    h.set(bsize)
  }
  useLayoutEffect(() => observeResize(rfmain.current, onResize))

  function onLoaded(v) {
    if (!v) return
    rfmain.current?.classList.add("mc-nav-loaded")
  }
  useMotionValueEvent(loaded, "change", onLoaded)

  function areTimestampsEqual(ts1, ts2, marginMs = 10000) {
    // Math.abs guarantees a positive difference regardless of which timestamp is larger
    return Math.abs(ts1 - ts2) <= marginMs
  }

  function goLast() {
    moving.set(lastFM())
  }

  function goToday() {
    moving.set(now())
  }

  function goNext() {
    moving.set(nextFM())
  }

  function onMoving(m) {
    if (areTimestampsEqual(m, nextFM())) {
      prevM.current.removeAttribute("disabled")
      nowM.current.removeAttribute("disabled")
      nextM.current.setAttribute("disabled", "")
      return
    }
    if (areTimestampsEqual(m, now())) {
      prevM.current.removeAttribute("disabled")
      nowM.current.setAttribute("disabled", "")
      nextM.current.removeAttribute("disabled")
      return
    }
    if (areTimestampsEqual(m, lastFM())) {
      prevM.current.setAttribute("disabled", "")
      nowM.current.removeAttribute("disabled")
      nextM.current.removeAttribute("disabled")
      return
    }
    prevM.current.removeAttribute("disabled")
    nowM.current.removeAttribute("disabled")
    nextM.current.removeAttribute("disabled")
  }
  useMotionValueEvent(moving, "change", onMoving)

  function onTransitionEnd(e) {
    if (ended.get() === 1 || e.target !== rfmain.current) return
    ended.set(1)
  }

  useLayoutEffect(() => {
    onMoving(moving.get())
  }, [])

  return (
    <Main
      className='dark-theme'
      ref={rfmain}
      onTransitionEnd={onTransitionEnd}
      style={{
        "--mc-nav-offset": offset,
      }}
    >
      <Overlay>
        <div></div>
      </Overlay>
      <Wrapper>
        <Nav>
          <NavButton ref={prevM} icon='none' className='translucent-light' aria-label={labels.last_full_moon} onClick={goLast}>
            <Icon type='movePrevious' aria-hidden={true} />
            <span className='label'>{labels.last_full_moon}</span>
            <MoonCalendarButtonImage />
          </NavButton>

          <NavButton ref={nowM} icon='none' className='filled translucent-light' onClick={goToday}>
            {labels.today}
          </NavButton>

          <NavButton ref={nextM} icon='none' className='translucent-light reverseIcon' aria-label={labels.next_full_moon} onClick={goNext}>
            <Icon type='moveNext' aria-hidden={true} />
            <span className='label'>{labels.next_full_moon}</span>
            <MoonCalendarButtonImage />
          </NavButton>
        </Nav>
        <Guide>
          <MoonphaseInstructions
            className="expand"
            labels={labels}
            locale={locale}
            timezone={props.timezone}
          />
        </Guide>
      </Wrapper>
    </Main>
  )
}
